import type { OdometerChangeReasonView } from "@credit-acceptance/step6-bff-client";
import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../../../../components/ui/radioGroup";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useOdometerChangeReasons } from "../api/useOdometerChangeReasons";

export const ODOMETER_DISCLOSURE_PANEL_ID = "odometer-disclosure-panel";

interface OdometerDisclosureModalProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** `vehicleOnDeal.odometerChangeReasonPk` — the deal's stored reason, or absent when none was recorded. */
  readonly odometerChangeReasonPk?: number | null;
}

/**
 * The row label caps2 showed for a reason. `description` is the user-facing
 * copy; `name` is the code behind it, used only as a fallback so a row never
 * renders blank when the BFF omits the description.
 * @param {OdometerChangeReasonView} reason - A single reason row.
 * @returns {string} The label to render.
 */
function reasonLabel(reason: OdometerChangeReasonView): string {
  return reason.description ?? reason.name ?? "";
}

/**
 * The reason radio list. Split out of the modal shell deliberately: it only
 * mounts when `DialogContent`'s portal is open, so the reason query never
 * fires — and no QueryClient is required — for a Vehicle section whose modal
 * was never opened.
 */
function OdometerReasonList({ odometerChangeReasonPk }: { readonly odometerChangeReasonPk?: number | null }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useOdometerChangeReasons();
  const reasons = data ?? [];
  // `== null` rather than `=== undefined`: the BFF serializes the pk as an
  // explicit JSON null when the deal has no recorded reason, and RadioGroup's
  // `value` needs a string either way. "" leaves every radio unchecked, which
  // is caps2's behavior for a deal with no reason.
  const selectedValue = odometerChangeReasonPk == null ? "" : String(odometerChangeReasonPk);

  return (
    <div className="flex flex-col gap-2">
      {isLoading && (
        <div className="text-muted-foreground flex items-center gap-1 text-xs">
          <Loader2 aria-hidden="true" className="size-3 animate-spin" />
          {t("vehicle.odometerDisclosure.loading")}
        </div>
      )}

      {isError && (
        <p role="alert" className="text-destructive text-xs">
          {t("vehicle.odometerDisclosure.loadError", { message: getErrorMessage(error) })}
        </p>
      )}

      {/* Spec §5.4: the list and selection "only populate when the reason list
          is non-empty" — an empty response shows copy, not a bare radio group. */}
      {!isLoading && !isError && reasons.length === 0 && (
        <p className="text-muted-foreground text-xs">{t("vehicle.odometerDisclosure.noReasons")}</p>
      )}

      {/* No visible group heading, so the radio group's accessible name comes
          from aria-label — a radio group with no name is unusable with a
          screen reader. */}
      {reasons.length > 0 && (
        <RadioGroup
          value={selectedValue}
          disabled
          aria-label={t("vehicle.odometerDisclosure.reasonLabel")}
          aria-readonly="true"
        >
          {reasons.map((reason) => {
            const value = String(reason.pk);
            return (
              <div key={value} className="flex items-center gap-2">
                <RadioGroupItem value={value} id={`odometer-reason-${value}`} />
                <label htmlFor={`odometer-reason-${value}`} className="text-xs">
                  {reasonLabel(reason)}
                </label>
              </div>
            );
          })}
        </RadioGroup>
      )}
    </div>
  );
}

/**
 * Odometer Disclosure modal (SVRS-55884) — the Workbench port of caps2's
 * `step6_Odometer_Mileage_Reason_Popup`, opened from the Odometer Disc Mileage
 * label link. Per the Vehicle tab spec §5.4 this is strictly view-only: every
 * radio is disabled and the selection simply reflects the deal's stored
 * `odometerChangeReasonPk`. The reason is set elsewhere, never here, so there
 * is no save path and no `onChange`.
 *
 * The reason list comes from `GET /api/v1/workbench/odometer-change-reasons`,
 * which already narrows to the reasons in effect today — rows render in server
 * order with no client-side filtering or sorting.
 *
 * Deliberately openable even when the section is read-only: spec §3.4's
 * Decision keeps the four informational popups reachable in read-only mode
 * (matching the Financial tab), unlike legacy which disabled the links.
 */
export function OdometerDisclosureModal({
  open,
  onOpenChange,
  odometerChangeReasonPk,
}: OdometerDisclosureModalProperties) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id={ODOMETER_DISCLOSURE_PANEL_ID} showCloseButton={false} className="gap-0 overflow-hidden p-0">
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">
            {t("vehicle.odometerDisclosure.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("vehicle.odometerDisclosure.description")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
            aria-label={t("vehicle.odometerDisclosure.close")}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        <div className="flex flex-col gap-3 p-4">
          <OdometerReasonList odometerChangeReasonPk={odometerChangeReasonPk} />
        </div>

        <DialogFooter className="justify-center border-t px-4 py-3 sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("vehicle.odometerDisclosure.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
